/**
 * `notifications` namespace — English canonical source.
 *
 * Two surfaces: the activity feed (routes/notifications.tsx) and the delivery-policy
 * matrix (features/notifications/policy.tsx). Covers page chrome, filter chips,
 * per-event-type labels (the taxonomy from §2), the channel × event routing grid,
 * priority floor, quiet hours, and the save/discard controls. Event-type labels are
 * consumed at the point of render (the `EVENT_META` table itself is not localized),
 * so both surfaces map an event type to one of the `event*` keys here. Generic
 * save-failure copy reuses `common.saveFailed`. `ko/notifications.ts` mirrors this
 * shape (a missing key is a type error).
 */
export const notifications = {
  // ── feed chrome ──
  eyebrow: "Activity",
  title: "Notifications",
  description: "Signals, order events, approvals, AI verdicts, and risk breaches.",
  markAllRead: "Mark all read",

  // ── feed empty / error ──
  loadError: "Could not load notifications. Try again.",
  emptyTitle: "Nothing here",
  emptyAll: "Activity from your strategies will show up here.",
  emptyFiltered: "No notifications match this filter.",

  // ── filter chips ──
  filterAll: "All",
  filterUnread: "Unread",

  // ── row affordances ──
  priorityHigh: "High",
  markRead: "Mark read",

  // ── event-type labels (§2 taxonomy) ──
  eventSignal: "Signal",
  eventOrderPlaced: "Order placed",
  eventOrderFilled: "Order filled",
  eventApprovalRequested: "Approval requested",
  eventAiVerdict: "AI verdict",
  eventRiskBreach: "Risk breach",
  eventError: "Error",

  // ── policy matrix ──
  policyEventCol: "Event",
  channelWeb: "Web",
  channelPush: "Push",
  channelEmail: "Email",
  channelToggleAria: "{event} via {channel}",
  minPriorityLabel: "Minimum priority",
  minPriorityHint: "Suppress anything below this floor across all channels.",
  priorityLowAll: "Low — everything",
  priorityNormalUp: "Normal and above",
  priorityHighOnly: "High only",
  quietHours: "Quiet hours",
  enable: "Enable",
  quietStartAria: "Quiet hours start",
  quietEndAria: "Quiet hours end",
  quietOff: "Off — deliver at any hour.",
  savePolicy: "Save policy",
  discard: "Discard",
  policyLoadError: "Could not load the notification policy.",
} as const;

export type NotificationsKey = keyof typeof notifications;
